import { BaseTag } from '@/components/chariot-ui';
import { format, isValid, parseISO } from 'date-fns';

import { Column } from '@/components/table/types';
import { ApiKey } from '@/types';
import { cn } from '@/utils/classname';
import { DEFAULT_FORMAT_TYPE, formatDate } from '@/utils/date.util';

/**
 * Extended ApiKey type with isExpired flag for UI display
 */
export type ApiKeyWithExpired = ApiKey & {
  isExpired: boolean;
};

/**
 * API Key table column definitions
 *
 * Columns:
 * 1. Name - API key name with disabled styling for expired keys
 * 2. ID - API key ID with disabled styling for expired keys
 * 3. Created - Creation date formatted with formatDate utility
 * 4. Expiration Date - Expiration date or "Expired" tag
 *
 * Features:
 * - Expired state styling (gray text) for expired keys
 * - BaseTag component for expired badge
 * - Date formatting with date-fns
 * - Consistent width classes for alignment
 *
 * Extracted from ApiKeys.tsx (Lines 64-137)
 * Used in: UserSettingsCards
 */
export const apiKeyColumns: Column<ApiKeyWithExpired>[] = [
  {
    label: 'Name',
    id: 'name',
    minWidth: 150,
    maxWidth: 250,
    cell: item => (
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'font-bold',
            item.isExpired && 'text-chariot-text-disabled'
          )}
        >
          {item.name}
        </span>
      </div>
    ),
  },
  {
    label: 'ID',
    id: 'id',
    minWidth: 200,
    maxWidth: 300,
    cell: item => (
      <span
        className={cn(
          'font-mono text-xs',
          item.isExpired && 'text-chariot-text-disabled'
        )}
      >
        {item.id}
      </span>
    ),
  },
  {
    label: 'Created',
    id: 'created',
    minWidth: 120,
    maxWidth: 150,
    cell: item => (
      <span className={cn(item.isExpired && 'text-chariot-text-disabled')}>
        {formatDate(item.created)}
      </span>
    ),
  },
  {
    label: 'Expiration Date',
    id: 'expires',
    minWidth: 140,
    maxWidth: 180,
    cell: item => {
      if (item.isExpired) {
        return (
          <BaseTag
            className="border-chariot-stroke-medium bg-chariot-status-medium text-chariot-text-primary py-0"
            size="xs"
          >
            Expired
          </BaseTag>
        );
      }

      if (!item.expires) {
        return <span>-</span>;
      }

      const date = parseISO(item.expires);
      if (!isValid(date)) {
        return <span>-</span>;
      }

      return <span>{format(date, DEFAULT_FORMAT_TYPE)}</span>;
    },
  },
];
